import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Lock, Mail, User, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Login({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Errore durante il login');
      }

      const data = await res.json();

      // Save token
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Call parent callback
      onLogin(data.user);

      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Errore di connessione. Verifica la tua rete.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Errore durante la registrazione');
      }

      setSuccess(data.message);
      setFormData({ email: '', password: '', nome: '', cognome: '' });
      
      // Switch to login after 3 seconds
      setTimeout(() => {
        setMode('login');
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/main-solution-logo.png" 
              alt="Main Solution" 
              className="h-16 w-auto"
            />
          </div>
          <h1 
            className="text-4xl font-bold mb-2"
            style={{ 
              background: 'linear-gradient(135deg, #00A3E0 0%, #0077BE 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            💙 Life
          </h1>
          <p className="text-muted-foreground">HR & HSE Portal</p>
        </div>

        {/* Card */}
        <div className="card">
          {/* Tabs */}
          <div className="flex border-b border-border mb-6">
            <button
              type="button"
              className={`flex-1 px-4 py-3 font-medium transition-colors ${
                mode === 'login'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => {
                setMode('login');
                setError('');
                setSuccess('');
              }}
              data-testid="tab-login"
            >
              <LogIn size={20} className="inline mr-2" />
              Accedi
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-3 font-medium transition-colors ${
                mode === 'register'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => {
                setMode('register');
                setError('');
                setSuccess('');
              }}
              data-testid="tab-register"
            >
              <UserPlus size={20} className="inline mr-2" />
              Registrati
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-4 flex items-start gap-2">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 text-green-800 px-4 py-3 rounded-lg mb-4 flex items-start gap-2">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div className="form-group">
                <label className="form-label">Email</label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    name="email"
                    className="form-input pl-10"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@azienda.it"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="password"
                    name="password"
                    className="form-input pl-10"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Accesso...' : 'Accedi'}
              </button>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <div className="relative">
                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      name="nome"
                      className="form-input pl-10"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      placeholder="Mario"
                      data-testid="register-nome"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Cognome</label>
                  <div className="relative">
                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      name="cognome"
                      className="form-input pl-10"
                      value={formData.cognome}
                      onChange={handleChange}
                      required
                      placeholder="Rossi"
                      data-testid="register-cognome"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Aziendale</label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    name="email"
                    className="form-input pl-10"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@azienda.it"
                    data-testid="register-email"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Usa solo email aziendale per registrarti
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="password"
                    name="password"
                    className="form-input pl-10"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="••••••••"
                    data-testid="register-password"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Minimo 6 caratteri
                </p>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'Registrazione...' : 'Registrati'}
              </button>

              <p className="text-xs text-center text-muted-foreground">
                La tua registrazione sarà approvata dall'amministratore
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Main Solution S.r.l. © 2015-2025
        </p>
      </div>
    </div>
  );
}

export default Login;
